import { arrayUnion, collection, doc, getDoc, getDocs, serverTimestamp, setDoc } from 'firebase/firestore';
import { onIdTokenChanged, type User } from 'firebase/auth';
import { auth, db, ensureUser } from '../firebase';
import { track } from '../analytics';
import { isMetaInAppBrowser } from './in-app-browser';
import { clearProfile, identityChange, loadProfile, mergeProfiles, withAccountCrew, onProfileSave, saveProfile, setProfileUid, toServerProfile, type Profile } from './state';

/**
 * The server copy of the road (16 §4). kindling-users/{uid}.profile is
 * canonical; the cache in localStorage is what the screens read, and every
 * saveProfile writes through here, coalesced. Boot reads the server copy once
 * and merges, so a cleared browser, a new phone, or a sign-in gets the road
 * back before the first screen decides what to show.
 */
export type HydrateMode =
  | 'merge'  // the usual boot: server and cache reconciled, higher madeDays wins
  | 'push'   // DEV ?day=N: the cache just written wins outright
  | 'reset'; // ?fresh: Day 1 again, on this device and on the server

let pending: Profile | null = null;
let timer: ReturnType<typeof setTimeout> | undefined;

/** A setDoc still unanswered after this long is reported: on a dead connection Firestore waits, it does not throw. */
const SLOW_WRITE_MS = 15_000;

/** Once per page per kind: a write-through that fails, fails on every save, and Day 1 saves on every tap. */
const reported = new Set<string>();
function reportOnce(event: string, props: Record<string, unknown>) {
  const key = `${event}:${JSON.stringify(props)}`;
  if (reported.has(key)) return;
  reported.add(key);
  track(event, { ...props, in_app: isMetaInAppBrowser() });
}

/**
 * Write the latest save through to kindling-users/{uid}.
 *
 * Until 2026-10-14 every way this could not happen was silent: nobody signed
 * in, a cache stamped with another uid, a rejected write. A Facebook visitor's
 * answers, intro-seen and dreams never reached the server (the account kept
 * only boot's first write, an empty road) and nothing anywhere said so. Each
 * of those is an event now, and a save made while nobody is signed in is held
 * for whoever is signed in next (onIdentity) instead of thrown away.
 */
async function flush(): Promise<void> {
  if (timer) { clearTimeout(timer); timer = undefined; }
  const p = pending; pending = null;
  if (!p) return;
  const user = auth.currentUser;
  if (!user) {
    // signed out under the page (onIdentity): keep it for whoever comes next
    pending = p;
    reportOnce('profile_write_skipped', { reason: 'signed_out' });
    return;
  }
  // Never write one person's road into another's account. Once onIdentity has
  // adopted any anonymous change, this is only the moment between signing into
  // an account that already has a feed and the reload onto it (Feed.tsx).
  if (p.uid && p.uid !== user.uid) { reportOnce('profile_write_skipped', { reason: 'other_uid' }); return; }
  const slow = setTimeout(() => reportOnce('profile_write_slow', { after_ms: SLOW_WRITE_MS }), SLOW_WRITE_MS);
  try {
    // mergeFields replaces `profile` whole, so a field cleared on the device clears on the server too
    await setDoc(
      doc(db, 'kindling-users', user.uid),
      { profile: toServerProfile(p), madeDays: p.madeDays, path: p.path, ...(p.name ? { name: p.name } : {}), updatedAt: serverTimestamp() },
      { mergeFields: ['profile', 'madeDays', 'path', ...(p.name ? ['name'] : []), 'updatedAt'] },
    );
  } catch (err) {
    console.warn('[kindling] profile write-through failed', err);
    reportOnce('profile_write_failed', { code: (err as { code?: string } | undefined)?.code || 'unknown' });
  } finally { clearTimeout(slow); }
}

function schedule(p: Profile) {
  pending = p;
  if (!timer) timer = setTimeout(() => { void flush(); }, 500);
}

onProfileSave(schedule);
if (typeof document !== 'undefined') document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') void flush(); });

// ---- whose road the cache is ------------------------------------------------

/** The identity the cache is claimed for. Null until hydrateProfile has run: before that, boot decides. */
let claimed: { uid: string; anon: boolean } | null = null;

function claim(user: Pick<User, 'uid' | 'isAnonymous'>) {
  claimed = { uid: user.uid, anon: user.isAnonymous };
  setProfileUid(user.uid, user.isAnonymous);
}

/**
 * The PostHog row for "the identity changed under a cached road", to count it:
 * how often, in which browsers, and what was done with the road. `adopted` is
 * somebody who would otherwise have been sent back to the intro; `cleared` is
 * the privacy rule doing its job; `lost` is a page signed out under itself.
 */
function trackIdentityChange(action: 'adopted' | 'cleared' | 'lost', when: 'boot' | 'session', cache: Profile, to: Pick<User, 'isAnonymous'> | null) {
  track('identity_changed', {
    action, when,
    from_anon: cache.uidAnon ?? null,
    to_anon: to ? to.isAnonymous : null,
    intro_seen: !!cache.introSeen,
    made_days: cache.madeDays,
    route: typeof window !== 'undefined' ? window.location.pathname : undefined,
    in_app: isMetaInAppBrowser(),
  });
}

/**
 * The road moves to the new anonymous uid (identityChange says when). The old
 * account is left as it is, and the new one notes where its road came from:
 * the posts, prints and characters the backend keeps under the old uid do not
 * move with the profile, and this is the thread a backend merge would follow.
 * The client wrote it, so the backend must not take it as proof of ownership.
 */
function adopt(cache: Profile, user: User, when: 'boot' | 'session') {
  claim(user);
  trackIdentityChange('adopted', when, cache, user);
  if (!cache.uid) return;
  setDoc(doc(db, 'kindling-users', user.uid), { adoptedFrom: arrayUnion(cache.uid) }, { merge: true })
    .catch((err) => console.warn('[kindling] adoptedFrom write failed', err));
}

/**
 * The identity changing while the page is open.
 *
 * Auth's IndexedDB persistence reads its record back every 800 ms, and a record
 * that reads back missing is taken as a sign-out in another tab: `currentUser`
 * goes null under the page. Kindling has no sign-out, so for an anonymous
 * person that is the browser losing the sign-in, and it used to mean every
 * later save went nowhere (flush found nobody) until a reload made a new person
 * of them. Now a new anonymous identity is made at once and the next call here
 * adopts the road into it, so the saves keep landing.
 *
 * A change to a real account (signing into a feed that already exists) is left
 * alone: the app reloads onto that account and boot clears the cache then.
 */
async function onIdentity(user: User | null): Promise<void> {
  if (!claimed) return;
  if (user && user.uid === claimed.uid) {
    // the same uid linked to Google or an email in place: no longer anonymous, and every later save says so
    if (user.isAnonymous !== claimed.anon) claim(user);
    if (pending) void flush();
    return;
  }
  const cache = loadProfile();
  if (!user) {
    trackIdentityChange('lost', 'session', cache, null);
    if (claimed.anon) await ensureUser().catch((err) => console.warn('[kindling] could not sign in again', err));
    return;
  }
  if (identityChange(cache, user) !== 'adopt') return;
  adopt(cache, user, 'session');
  saveProfile(cache); // stamped with the new uid now, and written through to it
}

// onIdTokenChanged rather than onAuthStateChanged: it also fires when the
// account is linked in place, which is when `isAnonymous` turns false.
onIdTokenChanged(auth, (user) => { void onIdentity(user); });

/**
 * Before this change the road lived only on the device; the backend kept one
 * doc per made day under kindling-users/{uid}/days. Rebuild what it can from
 * those, once, for anyone who was here before the server copy existed.
 */
async function fromLegacyDoc(uid: string, d: Record<string, unknown>): Promise<Partial<Profile>> {
  const days: Profile['days'] = {};
  try {
    const snap = await getDocs(collection(db, 'kindling-users', uid, 'days'));
    for (const x of snap.docs) {
      const n = Number(x.id); const v = x.data();
      const at = (v.madeAt as { toDate?: () => Date } | undefined)?.toDate?.();
      if (!Number.isFinite(n) || !v.challengeId || !at) continue;
      days[n] = { challengeId: String(v.challengeId), postId: v.postId ? String(v.postId) : undefined, madeOn: `${at.getFullYear()}-${String(at.getMonth() + 1).padStart(2, '0')}-${String(at.getDate()).padStart(2, '0')}` };
    }
  } catch (err) { console.warn('[kindling] legacy days read failed', err); }
  const anchor = d.anchor as { kind?: string; time?: string } | undefined;
  const madeOns = Object.values(days).map((x) => x.madeOn).sort();
  const madeDays = Number(d.madeDays) || Object.keys(days).length;
  return {
    madeDays, days,
    lastMadeOn: madeOns[madeOns.length - 1],
    name: typeof d.name === 'string' ? d.name : undefined,
    path: d.path === 'drawing' ? 'drawing' : 'writing',
    crew: d.crew as Profile['crew'],
    anchor: anchor?.kind as Profile['anchor'],
    anchorTime: anchor?.time,
    introSeen: madeDays > 0 || undefined,
  };
}

/** Read the server copy and reconcile it with this device's cache; returns what the screens should now see. */
export async function hydrateProfile(mode: HydrateMode = 'merge'): Promise<Profile> {
  const user = await ensureUser();
  let local = loadProfile();
  // A cache stamped with another uid: kept when an anonymous sign-in was lost
  // and replaced (same phone, same person), cleared when an account is on
  // either side (somebody else's road is never inherited). See identityChange.
  const change = identityChange(local, user);
  if (change === 'adopt') adopt(local, user, 'boot');
  else {
    claim(user);
    if (change === 'clear') { trackIdentityChange('cleared', 'boot', local, user); clearProfile(); local = loadProfile(); }
  }
  if (mode === 'reset') { clearProfile(); local = loadProfile(); }
  if (mode === 'reset' || mode === 'push') { saveProfile(local); await flush(); return loadProfile(); }

  let server: Partial<Profile> | null = null;
  let accountCrew: unknown;
  try {
    const snap = await getDoc(doc(db, 'kindling-users', user.uid));
    const d = snap.exists() ? (snap.data() as Record<string, unknown>) : null;
    accountCrew = d?.crew;
    if (d?.profile) server = d.profile as Partial<Profile>;
    else if (d && (d.madeDays || d.name || d.crew)) server = await fromLegacyDoc(user.uid, d);
  } catch (err) {
    console.warn('[kindling] profile read failed; running from the cache', err);
    return local;
  }
  const merged = withAccountCrew(mergeProfiles(local, server), accountCrew);
  saveProfile(merged); // stamps the uid and writes through, so a device that had the only copy now has a server one
  return merged;
}
